/**
 * Maps a Frappe `Item` document (module `Quoflow`, `rfqdev.etoserp.com`) onto Bosun's `Item`
 * domain type. This is the ONLY place an `Item` field name may appear — screens and lib/
 * derivations never see Frappe shapes.
 *
 * NOT vestigial — task 12 kept this file. `useItems.ts`'s `useItemsByCode`/`useItem` (item-by-code
 * reads behind `/rfq`, `/compare`, `/quote`, `/revisions`, `SupplierQuotationsPage`) are the last
 * live callers: no `quoflow.*` batch/single item-by-code endpoint exists yet. See
 * `src/lib/api/frappe.ts`'s own header and task-12-report.md's backend-gap list.
 *
 * Verified field-by-field against `docs/frappe-schema/quoflow-schema.json`: the real `Item` is a
 * lean **11 fields** — `item_code`, `item_name`, `item_group`, `description`, `stock_uom`,
 * `is_stock_item`, `is_fixed_asset`, `impa_number`, `uoms` (Table → `Item UOM`),
 * `reorder_levels` (Table → `Item Reorder`), `platform_id`. `Item UOM` itself is 2 fields: `uom`,
 * `conversion_factor`. (`docs/frappe-schema/schema.json` is a different server's dump and was not
 * consulted for any name here.)
 *
 * Judgment calls a future reader needs to know about:
 *
 * 1. `category` is OMITTED — Quoflow has no category concept anywhere in the schema (Q15 in
 *    docs/bosun-frappe-open-questions.md, open for the business, no longer blocking: see
 *    `Item.category` in `src/lib/types.ts`). `/rfq` shows the real `group` instead.
 * 2. `group` passes `item_group` straight through — `ItemGroup` is `string` now that the server's
 *    real tree (1,240 groups, IMPA-style names) is known to share nothing with the old six-value
 *    placeholder union.
 * 3. Several `Item` properties have no source on this lean doctype at all and get a defaulted,
 *    documented value rather than a fabricated one:
 *    - `on_hand` → **OMITTED**, not `0`. `Item` carries no stock quantity; `Opportunity
 *      Item.stock_qty` is a per-enquiry-line figure, not a per-item one, so it cannot stand in
 *      here. It used to default to `0`, which is not "unknown" — it reads as "none in stock", and
 *      `exceedsOnHand` in src/lib/format.ts was consequently true for EVERY line with a qty,
 *      putting a red "⚠ N pcs exceeds available 0" warning on every row of `/rfq`. `Item.on_hand`
 *      is optional now: absent means no stock source, the warning does not fire, and the badge
 *      renders "—".
 *    - `base_price` → `null`. No `standard_rate` / `valuation_rate` on `Item`. Pricing lives on
 *      the separate `Item Price` doctype — a caller wanting a real price fetches that.
 *    - `candidate_suppliers` → `[]`. Lives on `Item Group.suppliers` (→ `Item Group Supplier`), a
 *      different doctype keyed by group, not by item — a caller fetches it separately.
 *    - `contracts` → `[]`. Lives on `Blanket Order` / `Blanket Order Item`, a different doctype —
 *      a caller fetches it separately.
 *    None of these four cites a numbered open question: they are not gaps in the schema, they are
 *    Bosun asking one doctype for data that genuinely lives on another.
 */

import { childRows, num, str } from './coerce'
import type { Item, UomConversion } from '../../types'

export const ITEM_DOCTYPE = 'Item'

/**
 * The DOCUMENT fields `toItem` reads — same convention as `RFQ_DOC_FIELDS` and `SQ_DOC_FIELDS`.
 * `uoms` IS named here, because a `get_document` payload carries it and `toUomConversions` reads
 * it; that is exactly what makes this list unusable for a `get_list` read, where naming a child
 * table gets you a field that silently arrives absent. Use `ITEM_LIST_FIELDS` for lists.
 */
export const ITEM_DOC_FIELDS: string[] = [
  'item_code',
  'item_name',
  'item_group',
  'description',
  'stock_uom',
  'impa_number',
  'uoms',
]

/**
 * The `fields` to request on a LIST read of `Item` — the scalars above, without the `uoms` child
 * table. `get_list` never returns child tables, so every row's `uom_conversions` comes back `[]`
 * on this path (see `useItems`' own header); that is a property of the read, not a mock shortcut.
 *
 * This exists because `ITEM_DOC_FIELDS` was being passed as list `fields` in `useItems.ts`, which
 * its own doc comment forbids.
 */
export const ITEM_LIST_FIELDS: string[] = [
  'item_code',
  'item_name',
  'item_group',
  'description',
  'stock_uom',
  'impa_number',
]

/**
 * `Item UOM` rows, excluding the identity row whose `uom` equals `stock_uom` — that row just
 * restates the base unit at a conversion factor of 1 and would otherwise show up as a redundant
 * "1 pcs = 1 pcs" conversion in the UI. A row with a blank `uom` is dropped the same way `str`
 * drops any other blank field: it converts to nothing usable.
 */
function toUomConversions(doc: Record<string, unknown>): UomConversion[] {
  const stockUom = str(doc.stock_uom)
  return childRows(doc.uoms)
    .map((row) => ({ uom: str(row.uom), factor: num(row.conversion_factor) }))
    .filter((row) => row.uom !== '' && row.uom !== stockUom)
}

/** Maps one `Item` document onto one Bosun `Item`. */
export function toItem(doc: Record<string, unknown>): Item {
  return {
    code: str(doc.item_code),
    name: str(doc.item_name),
    // `category` omitted entirely — see module header, point 1.
    group: str(doc.item_group),
    base_uom: str(doc.stock_uom),
    // Carried straight through; empty string when the server has none recorded, same as every
    // other optional Data field this mapper reads.
    impa_number: str(doc.impa_number),
    uom_conversions: toUomConversions(doc),
    // `on_hand` is OMITTED, deliberately — not 0. Item carries no stock quantity; see module
    // header, point 3, and `Item.on_hand` in src/lib/types.ts.
    base_price: null, // no standard_rate / valuation_rate — lives on Item Price, a separate fetch
    candidate_suppliers: [], // lives on Item Group.suppliers (Item Group Supplier), a separate fetch
    contracts: [], // lives on Blanket Order / Blanket Order Item, a separate fetch
  }
}
